import React, { useEffect, useState } from 'react'
import { Button } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import Select from 'react-select'
import { PermissionAction, permissionsService, PermissionType } from '../../services/permissions.service'
import { tagService } from '../../services/tag.service'
import { Tag as TagType } from '../../types'
import { flattenTags } from '../../utils/flattenTags'
import './InputTags.scss'

interface InputTagsProps {
  value?: number[]
  onChange?: (value: number[]) => void
  disabled?: boolean
  title?: string
  hint?: string
  multiple?: boolean
  options?: Array<{
    value: number
    label: string
    color?: string
    text_color?: string
    parent?: number | null
  }>
  showFilter?: boolean
  onFilterDocuments?: (tags: number[]) => void
  onCreateNew?: () => void
  hideAddButton?: boolean
  suggestions?: number[]
  horizontal?: boolean
  menuPortalTarget?: HTMLElement | null
}

export const InputTags: React.FC<InputTagsProps> = ({
  value = [],
  onChange,
  disabled = false,
  title,
  hint,
  multiple = true,
  options,
  showFilter = false,
  onFilterDocuments,
  onCreateNew,
  hideAddButton = false,
  suggestions,
  horizontal = false,
  menuPortalTarget,
}) => {
  const { t } = useTranslation()
  const [tags, setTags] = useState<TagType[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (options) {
      const mappedOptions: TagType[] = options.map((opt) => ({
        id: opt.value,
        name: opt.label,
        color: opt.color || '#6c757d',
        text_color: opt.text_color || '#ffffff',
        is_inbox_tag: false,
        parent: opt.parent ?? null,
        matching_algorithm: 1,
        match: '',
        is_insensitive: true,
      }))
      setTags(mappedOptions)
      return
    }

    const loadTags = async () => {
      if (!permissionsService.currentUserCan(PermissionAction.View, PermissionType.Tag)) {
        setTags([])
        return
      }
      setLoading(true)
      try {
        const allTags = await tagService.listAll()
        const flattened = flattenTags(allTags || [])
        setTags(flattened)
      } catch (error) {
      } finally {
        setLoading(false)
      }
    }
    loadTags()
  }, [options])

  const getTag = (id: number): TagType | undefined => {
    return tags.find((tag) => tag.id === id)
  }

  const getParentChain = (id: number): TagType[] => {
    const chain: TagType[] = []
    let current = getTag(id)
    const guard = new Set<number>()
    while (current?.parent) {
      if (guard.has(current.parent)) break
      guard.add(current.parent)
      const parent = getTag(current.parent)
      if (!parent) break
      chain.unshift(parent)
      current = parent
    }
    return chain
  }

  const addParents = (tagIds: number[]): number[] => {
    const result = new Set(tagIds)
    tagIds.forEach((tagId) => {
      const tag = getTag(tagId)
      if (tag?.parent) {
        const parent = getTag(tag.parent)
        if (parent && !result.has(parent.id)) {
          result.add(parent.id)
          const parentChain = addParents([parent.id])
          parentChain.forEach((id) => result.add(id))
        }
      }
    })
    return Array.from(result)
  }

  const removeChildren = (tagIDs: number[], tag: TagType): number[] => {
    const childTags = tags.filter((t) => t.parent === tag.id)
    if (childTags.length > 0) {
      const childIDs = childTags.map((child) => child.id)
      tagIDs = tagIDs.filter((id) => !childIDs.includes(id))
      for (const child of childTags) {
        tagIDs = removeChildren(tagIDs, child)
      }
    }
    return tagIDs
  }

  const handleChange = (selectedOptions: any) => {
    if (multiple) {
      const selectedTags = selectedOptions ? selectedOptions.map((option: any) => option.value) : []
      const tagsWithParents = addParents(selectedTags)
      onChange?.(tagsWithParents)
    } else {
      const selectedTag = selectedOptions ? selectedOptions.value : undefined
      const tagsWithParents = selectedTag ? addParents([selectedTag]) : []
      onChange?.(tagsWithParents)
    }
  }

  const handleRemoveTag = (tagId: number) => {
    const tag = getTag(tagId)
    if (!tag) return

    let newValue = value.filter((id) => id !== tagId)
    newValue = removeChildren(newValue, tag)
    onChange?.(newValue)
  }

  const getValue = () => {
    if (multiple) {
      return tags
        .filter((tag) => value.includes(tag.id))
        .map((tag) => ({
          value: tag.id,
          label: tag.name,
          color: tag.color,
          text_color: tag.text_color,
        }))
    } else {
      const selectedTag = tags.find((tag) => value.includes(tag.id))
      return selectedTag ? { value: selectedTag.id, label: selectedTag.name } : null
    }
  }

  const customStyles = {
    multiValue: (provided: any, state: any) => ({
      ...provided,
      backgroundColor: (state.data as any).color || '#6c757d',
      color: (state.data as any).text_color || '#ffffff',
      borderRadius: '0.375rem',
      padding: '0.25rem 0.5rem',
      margin: '0.125rem',
    }),
    multiValueLabel: (provided: any, state: any) => ({
      ...provided,
      color: (state.data as any).text_color || '#ffffff',
      fontSize: '0.875rem',
    }),
    multiValueRemove: (provided: any, state: any) => ({
      ...provided,
      color: (state.data as any).text_color || '#ffffff',
      ':hover': {
        backgroundColor: 'rgba(0, 0, 0, 0.1)',
        color: (state.data as any).text_color || '#ffffff',
      },
    }),
    menuPortal: (provided: any) => ({
      ...provided,
      zIndex: 1055,
    }),
  }

  const getSuggestions = () => {
    if (!suggestions || suggestions.length === 0) return []
    return tags.filter((tag) => suggestions.includes(tag.id) && !value.includes(tag.id))
  }

  return (
    <div className={`mb-0 docdot-input-tags ${disabled ? 'disabled' : ''} ${horizontal ? 'row' : ''}`}>
      {title && (
        <div className={horizontal ? 'col-md-3 d-flex align-items-center' : ''}>
          <label className={`form-label ${horizontal ? 'mb-md-0' : 'mb-2'}`} htmlFor="tags">
            {title}
          </label>
        </div>
      )}
      <div className={`position-relative ${horizontal && title ? 'col-md-9' : ''}`}>
        <div className={`input-group flex-nowrap ${horizontal ? 'input-group-sm' : ''}`}>
          <Select
            isMulti={multiple}
            options={tags.map((tag) => ({
              value: tag.id,
              label: tag.name,
              color: tag.color,
              text_color: tag.text_color,
              parent: tag.parent,
            }))}
            value={getValue()}
            onChange={handleChange}
            placeholder={t('select_tags')}
            className="react-select-container"
            classNamePrefix="react-select"
            isClearable={true}
            closeMenuOnSelect={false}
            isDisabled={disabled}
            isLoading={loading}
            styles={customStyles}
            menuPortalTarget={menuPortalTarget || undefined}
            menuPosition={menuPortalTarget ? 'fixed' : 'absolute'}
            components={{
              MultiValue: ({ data }) => {
                const tagData = getTag(data.value)
                return (
                  <div
                    className="tag-wrap btn p-0 d-flex align-items-center justify-content-center"
                    style={{
                      backgroundColor: tagData?.color || '#6c757d',
                      color: tagData?.text_color || '#ffffff',
                      borderRadius: '0.375rem',
                      padding: '0.1875rem 0.375rem',
                      margin: '0.0625rem',
                    }}
                  >
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation()
                        handleRemoveTag(data.value)
                      }}
                      onMouseDown={(e) => e.stopPropagation()}
                      className="btn p-0 d-flex align-items-center"
                      style={{
                        color: tagData?.text_color || '#ffffff',
                        padding: '0',
                        margin: '0 2px',
                        gap: '0.125rem',
                      }}
                      title={t('remove_tag')}
                    >
                      <i className="bi bi-x" style={{ fontSize: '0.7rem', display: 'flex', alignItems: 'center' }}></i>
                      <span style={{ fontSize: '0.65rem', padding: '0.0625rem 0' }}>{data.label}</span>
                    </button>
                  </div>
                )
              },
              Option: ({ data, innerProps, innerRef, isFocused }) => {
                const tagData = getTag(data.value)
                const hasParent = tagData?.parent
                const parentChain = hasParent ? getParentChain(data.value) : []

                return (
                  <div
                    ref={innerRef}
                    {...innerProps}
                    className={`tag-option-row d-flex align-items-center ${isFocused ? 'react-select__option--is-focused' : ''}`}
                  >
                    {hasParent && (
                      <>
                        <i className="bi bi-list-nested me-1 hierarchy-indicator" style={{ marginLeft: '8px' }}></i>
                        <span className="hierarchy-reveal d-flex align-items-center">
                          <span className="parents d-flex align-items-center">
                            {parentChain.map((parent) => (
                              <React.Fragment key={parent.id}>
                                <span
                                  className="badge me-1"
                                  style={{
                                    backgroundColor: parent.color,
                                    color: parent.text_color,
                                    marginTop: '6px',
                                    marginBottom: '6px',
                                    marginLeft: '10px',
                                  }}
                                >
                                  {parent.name}
                                </span>
                                <i
                                  className="bi bi-chevron-right me-1"
                                  style={{ fontSize: '0.8em', width: '0.8em', height: '0.8em', marginLeft: '8px' }}
                                ></i>
                              </React.Fragment>
                            ))}
                          </span>
                        </span>
                      </>
                    )}
                    <span
                      className="badge current-tag d-flex"
                      style={{
                        backgroundColor: tagData?.color || '#6c757d',
                        color: tagData?.text_color || '#ffffff',
                        marginTop: '6px',
                        marginBottom: '6px',
                        marginLeft: '10px',
                      }}
                    >
                      {data.label}
                    </span>
                  </div>
                )
              },
              ClearIndicator: ({ innerProps }) => (
                <div {...innerProps} className="react-select__clear-indicator" title={t('clear_all')}>
                  <i className="bi bi-x" style={{ fontSize: '1rem' }}></i>
                </div>
              ),
            }}
          />
          {!hideAddButton && onCreateNew && (
            <Button variant="outline-secondary" size="sm" type="button" onClick={onCreateNew} disabled={disabled} title={t('create_new_tag')}>
              <i className="bi bi-plus" style={{ width: '1.2em', height: '1.2em' }}></i>
            </Button>
          )}
          {showFilter && onFilterDocuments && (
            <Button
              variant="outline-secondary"
              size="sm"
              type="button"
              onClick={() => onFilterDocuments(value || [])}
              disabled={disabled || !value || value.length === 0}
              title={t('show_filter')}
            >
              <i className="bi bi-funnel" style={{ width: '1.2em', height: '1.2em' }}></i>
            </Button>
          )}
        </div>
        {hint && <small className="form-text text-muted">{hint}</small>}
        {getSuggestions().length > 0 && (
          <div className="mt-2">
            <small className="text-muted me-2">{t('suggestions')}:</small>
            {getSuggestions().map((tag) => (
              <button
                key={tag.id}
                type="button"
                className="btn btn-sm btn-link p-0 me-2"
                onClick={() => {
                  const newValue = [...(value || []), tag.id]
                  onChange?.(newValue)
                }}
                disabled={disabled}
              >
                <span
                  className="badge"
                  style={{
                    backgroundColor: tag.color,
                    color: tag.text_color || '#fff',
                  }}
                >
                  {tag.name}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
